import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { ArrowLeft, Plus, Wrench, Pencil } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TradeManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tradeType, setTradeType] = useState('in_house');
  const [materialPct, setMaterialPct] = useState(40);
  const [labourPct, setLabourPct] = useState(35);
  const [marginPct, setMarginPct] = useState(25);
  const [defaultHsn, setDefaultHsn] = useState('');
  const [hsnDescription, setHsnDescription] = useState('');

  useEffect(() => {
    fetchTrades();
  }, []);

  const fetchTrades = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/trades`, {
        withCredentials: true,
      });
      setTrades(data);
    } catch (error) {
      toast.error('Failed to load trades');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setTradeType('in_house');
    setMaterialPct(40);
    setLabourPct(35);
    setMarginPct(25);
    setDefaultHsn('');
    setHsnDescription('');
    setEditingTrade(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate percentages for in_house
    if (tradeType === 'in_house') {
      const total = parseFloat(materialPct) + parseFloat(labourPct) + parseFloat(marginPct);
      if (Math.abs(total - 100) > 0.01) {
        toast.error(`Percentages must sum to 100% (currently ${total.toFixed(1)}%)`);
        return;
      }
    }

    const payload = {
      name,
      description,
      trade_type: tradeType,
      material_percentage: parseFloat(materialPct),
      labour_percentage: parseFloat(labourPct),
      margin_percentage: parseFloat(marginPct),
      default_hsn_code: defaultHsn,
      hsn_description: hsnDescription,
    };

    try {
      if (editingTrade) {
        await axios.put(
          `${BACKEND_URL}/api/trades/${editingTrade.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Trade updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/trades`,
          payload,
          { withCredentials: true }
        );
        toast.success('Trade created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchTrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save trade');
    }
  };

  const handleEdit = (trade) => {
    setEditingTrade(trade);
    setName(trade.name);
    setDescription(trade.description);
    setTradeType(trade.trade_type);
    setMaterialPct(trade.material_percentage);
    setLabourPct(trade.labour_percentage);
    setMarginPct(trade.margin_percentage);
    setDefaultHsn(trade.default_hsn_code || '');
    setHsnDescription(trade.hsn_description || '');
    setDialogOpen(true);
  };

  const getTradeTypeLabel = (type) => {
    switch (type) {
      case 'in_house':
        return 'In-house';
      case 'subcontract_with_material':
        return 'Subcontract (with material)';
      case 'subcontract_without_material':
        return 'Subcontract (without material)';
      default:
        return type;
    }
  };

  const getTradeTypeBadge = (type) => {
    switch (type) {
      case 'in_house':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'subcontract_with_material':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'subcontract_without_material':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      default:
        return 'bg-zinc-100 text-zinc-800 border-zinc-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-pulse text-zinc-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3"
            data-testid="back-button"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
            Trade Management
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Configure trade types and cost split percentages
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-800">Trades</h2>
            <p className="text-sm text-zinc-600 mt-1">{trades.length} total trades</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-none"
                data-testid="add-trade-button"
              >
                <Plus size={20} className="mr-2" />
                Add Trade
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-zinc-900">
                  {editingTrade ? 'Edit Trade' : 'Add New Trade'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                      Trade Name *
                    </Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                      data-testid="trade-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                      Trade Type *
                    </Label>
                    <select
                      value={tradeType}
                      onChange={(e) => setTradeType(e.target.value)}
                      className="mt-2 w-full bg-zinc-50 border border-zinc-200 focus:border-blue-600 rounded-none p-3 text-base"
                      data-testid="trade-type-select"
                    >
                      <option value="in_house">In-house (Material + Labour + Margin)</option>
                      <option value="subcontract_with_material">Subcontract with Material (Margin only)</option>
                      <option value="subcontract_without_material">Subcontract without Material (We supply material)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                    Description
                  </Label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="mt-2 w-full bg-zinc-50 border border-zinc-200 focus:border-blue-600 rounded-none p-3 text-base"
                    data-testid="description-input"
                  />
                </div>
                
                <div className="border-t border-zinc-200 pt-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-3">
                    Cost Split Percentages
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                        Material %
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={materialPct}
                        onChange={(e) => setMaterialPct(e.target.value)}
                        disabled={tradeType === 'subcontract_with_material'}
                        className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                        data-testid="material-pct-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                        Labour %
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={labourPct}
                        onChange={(e) => setLabourPct(e.target.value)}
                        disabled={tradeType === 'subcontract_with_material'}
                        className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                        data-testid="labour-pct-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                        Margin %
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={marginPct}
                        onChange={(e) => setMarginPct(e.target.value)}
                        className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                        data-testid="margin-pct-input"
                      />
                    </div>
                  </div>
                  {tradeType === 'in_house' && (
                    <p className="text-sm text-zinc-600 mt-2">
                      Total: {(parseFloat(materialPct) + parseFloat(labourPct) + parseFloat(marginPct)).toFixed(1)}% 
                      {Math.abs((parseFloat(materialPct) + parseFloat(labourPct) + parseFloat(marginPct)) - 100) > 0.01 && 
                        <span className="text-red-600 font-medium"> (must equal 100%)</span>
                      }
                    </p>
                  )}
                </div>

                <div className="border-t border-zinc-200 pt-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-3">
                    HSN / SAC Code
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                        Default HSN/SAC Code
                      </Label>
                      <Input
                        value={defaultHsn}
                        onChange={(e) => setDefaultHsn(e.target.value)}
                        placeholder="e.g. 441231"
                        className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                        data-testid="hsn-code-input"
                      />
                      <p className="text-[10px] text-zinc-400 mt-1">Minimum 6 digits. Applied as default to BOQ items under this trade.</p>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                        HSN Description
                      </Label>
                      <Input
                        value={hsnDescription}
                        onChange={(e) => setHsnDescription(e.target.value)}
                        placeholder="e.g. Plywood, veneered panels"
                        className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 rounded-none p-3"
                        data-testid="hsn-description-input"
                      />
                    </div>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-6"
                  data-testid="submit-trade-button"
                >
                  {editingTrade ? 'Update Trade' : 'Add Trade'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trades.map((trade) => (
            <div
              key={trade.id}
              className="bg-white border border-zinc-200 p-6 hover:shadow-sm transition-shadow duration-200"
              data-testid={`trade-card-${trade.id}`}
            >
              <div className="flex justify-between items-start mb-3">
                <Wrench size={32} className="text-blue-700" />
                <span className={`text-xs font-medium px-2 py-1 border ${getTradeTypeBadge(trade.trade_type)}`}>
                  {getTradeTypeLabel(trade.trade_type)}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-zinc-800 mb-2">{trade.name}</h3>
              <p className="text-sm text-zinc-600 mb-4">{trade.description}</p>
              
              <div className="border-t border-zinc-200 pt-3 mb-4">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-zinc-500">Material</span>
                    <p className="font-bold text-zinc-900">{trade.material_percentage}%</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Labour</span>
                    <p className="font-bold text-zinc-900">{trade.labour_percentage}%</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Margin</span>
                    <p className="font-bold text-blue-700">{trade.margin_percentage}%</p>
                  </div>
                </div>
                {trade.default_hsn_code && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="text-xs font-mono bg-green-50 text-green-700 px-1.5 py-0.5 border border-green-200" data-testid={`hsn-badge-${trade.id}`}>
                      HSN: {trade.default_hsn_code}
                    </span>
                    {trade.hsn_description && <span className="text-[10px] text-zinc-500">{trade.hsn_description}</span>}
                  </div>
                )}
              </div>
              
              <Button
                onClick={() => handleEdit(trade)}
                variant="outline"
                size="sm"
                className="w-full border-zinc-300 rounded-none hover:bg-zinc-100"
                data-testid={`edit-trade-${trade.id}`}
              >
                <Pencil size={16} className="mr-1" />
                Edit Settings
              </Button>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};
